import { LoadAdjustmentFormTypeEnum } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/LoadPlanningAdjustment.types'
import { addDays, endOfDay, isSameDay, startOfDay, subDays } from 'date-fns'
import type { TFunction } from 'react-i18next'

export const ADJUSTMENT_TYPE_LABEL_MAP = (t: TFunction<'translation', undefined>) => ({
  [LoadAdjustmentFormTypeEnum.REFERENCE_PERIOD]: t('components.procurement.forecasting.select_labels.adjustment_type.reference_period'),
  [LoadAdjustmentFormTypeEnum.COMPANY_HOLIDAYS]: t('components.procurement.forecasting.select_labels.adjustment_type.company_holidays'),
  [LoadAdjustmentFormTypeEnum.QUANTITY_ADJUSTMENT]: t(
    'components.procurement.forecasting.select_labels.adjustment_type.quantity_adjustment'
  ),
  [LoadAdjustmentFormTypeEnum.RENEWABLE_PRODUCTION]: t('components.procurement.forecasting.select_labels.adjustment_type.renewable'),
})

export const loadAdjustmentTypeSelectOptions = (labelMap: Record<string, string>) =>
  Object.values(LoadAdjustmentFormTypeEnum).map((item: string) => ({ label: labelMap[item], value: item }))

const parseDateInputToLocalDate = (value?: string): Date | null => {
  if (!value) return null

  // Date-only format should be treated as a local calendar day, not as UTC midnight.
  // This avoids accidental off-by-one shifts when comparing with ISO timestamps (e.g. "2026-12-28T23:00:00Z").
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [yearStr, monthStr, dayStr] = value.split('-')
    const year = Number(yearStr)
    const month = Number(monthStr)
    const day = Number(dayStr)
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null
    return new Date(year, month - 1, day)
  }

  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

export const isSameCalendarDay = (a?: string, b?: string): boolean => {
  const aDate = parseDateInputToLocalDate(a)
  const bDate = parseDateInputToLocalDate(b)
  if (!aDate || !bDate) return false
  return isSameDay(aDate, bDate)
}

export const toExclusiveEndDateIso = (date: string | undefined): string | undefined => {
  if (!date) return date
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return date
  return startOfDay(addDays(parsed, 1)).toISOString()
}

export const fromExclusiveEndDateIso = (date: string | undefined): string | undefined => {
  if (!date) return date
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return date
  return endOfDay(subDays(parsed, 1)).toISOString()
}
